import { useMemo, useState, useEffect, useCallback } from 'react';

import useChangePage from 'hooks/useChangePage';
import useGetUserOrgDetails from 'hooks/queries/useGetUserOrgDetails';
import useQueryParams from 'hooks/useQueryParams';

import { useOrgPricing } from '../../SelectPlan';

import { formatUserDetails } from '../utils';

const userInitDetails = {
  businessName: '',
  email: '',
  firstName: '',
  lastName: '',
  organizationUid: '',
  phone: '',
  uid: '',
};

export const useLoadCheckoutData = () => {
  const changePage = useChangePage();
  const {
    error: userOrgDetailsError,
    loading: dataLoading,
    userOrgDetails = {},
  } = useGetUserOrgDetails();
  const {
    loading: orgPricesLoading,
    orgPricePackages,
    orgPrices,
  } = useOrgPricing();
  const { value: locationUid } = useQueryParams('locationUid');
  const { value: redirectUrl } = useQueryParams('redirectUrl');
  const { value: shouldDisplayPage } = useQueryParams('sd');
  const { value: showDormantState } = useQueryParams('ds');
  const { value: showSubCanceled } = useQueryParams('sb');

  const [businessAddress, setBusinessAddress] = useState({});
  const [userDetails, setUserDetails] = useState(userInitDetails);

  useEffect(() => {
    // redirect to dormant page
    if (showDormantState === 'display' || showSubCanceled === 'display') {
      changePage('/dormant');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDormantState, showSubCanceled]);

  const isUserDataValid = useMemo(() => {
    const hasUserAndDataLoaded = !dataLoading && !!userOrgDetails.uid;

    return shouldDisplayPage || 'display' === showDormantState
      ? hasUserAndDataLoaded
      : hasUserAndDataLoaded && !userOrgDetails.hasPaymentMethod;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoading, userOrgDetails.hasPaymentMethod, userOrgDetails.uid]);

  const setAndFormatCheckoutData = useCallback(() => {
    const {
      country,
      locality,
      postalCode,
      region,
      streetNumber,
      ...userDetails
    } = userOrgDetails || {};

    setUserDetails(formatUserDetails(userDetails));

    !!postalCode &&
      setBusinessAddress({
        address: streetNumber,
        city: locality,
        country,
        state: region,
        zip: postalCode,
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOrgDetails]);

  useEffect(() => {
    if (isUserDataValid) {
      setAndFormatCheckoutData();
    }
  }, [isUserDataValid, setAndFormatCheckoutData]);

  return useMemo(
    () => ({
      businessAddress,
      dataLoading: dataLoading || orgPricesLoading,
      isUserDataValid,
      locationUid: userOrgDetails.locationUid || locationUid,
      orgPricePackages,
      orgPrices,
      redirectUrl,
      setUserDetails,
      userDetails,
      userOrgDetailsError,
    }),
    [
      businessAddress,
      dataLoading,
      isUserDataValid,
      locationUid,
      orgPricePackages,
      orgPrices,
      orgPricesLoading,
      redirectUrl,
      userDetails,
      userOrgDetails.locationUid,
      userOrgDetailsError,
    ]
  );
};
